/**
 * Utilities for retrieving session data from GTM/GA4
 * Used for sending in API request headers
 */

const GA_MEASUREMENT_ID = 'G-ZYJW6Z08S4';

/**
 * GA4 data retrieval result
 */
export interface GA4SessionData {
  sessionId: string | null;
  clientId: string | null;
}

/** Max wait for gtag('get', ...) callbacks; prevents blocking API requests when GA/GTM callback never fires. */
const GA4_GET_TIMEOUT_MS = 1500;

function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => {
      setTimeout(resolve, ms, fallback);
    }),
  ]);
}

/**
 * Retrieves GA4 session_id and client_id through GTM
 * Optimized version that retrieves both values at once
 * Attempts to retrieve via gtag (if available through GTM) or returns null.
 * Uses a timeout so this never blocks API requests when gtag callback never fires (e.g. GA not ready).
 */
export async function getGA4SessionData(): Promise<GA4SessionData> {
  if (typeof window === 'undefined') {
    return { sessionId: null, clientId: null };
  }

  // Try to retrieve via gtag (GTM may provide access to gtag)
  if (typeof window.gtag !== 'function') {
    return { sessionId: null, clientId: null };
  }

  try {
    // Retrieve both values in parallel with timeout so we never hang
    const [sessionId, clientId] = await Promise.all([
      withTimeout(
        new Promise<string | null>((resolve) => {
          if (typeof window.gtag === 'function') {
            window.gtag('get', GA_MEASUREMENT_ID, 'session_id', (id: string) => {
              resolve(id || null);
            });
          } else {
            resolve(null);
          }
        }),
        GA4_GET_TIMEOUT_MS,
        null,
      ),
      withTimeout(
        new Promise<string | null>((resolve) => {
          if (typeof window.gtag === 'function') {
            window.gtag('get', GA_MEASUREMENT_ID, 'client_id', (clientId: string) => {
              resolve(clientId || null);
            });
          } else {
            resolve(null);
          }
        }),
        GA4_GET_TIMEOUT_MS,
        null,
      ),
    ]);

    return { sessionId, clientId };
  } catch (e) {
    console.warn('Failed to get GA4 session data from GTM:', e);
    return { sessionId: null, clientId: null };
  }
}
